import { Dashboard } from '@/common/dashboard/Dashboard';
import { DashboardDataProvider } from '@/common/dashboard/DashboardDataContext';
import { useForm } from '@/common/form/FormContext';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { useEffect, useMemo, useState } from 'react';
import { DemGtnSummaryFilters } from './DemGtnSummaryFilters';
import { DemGtnSummaryGridCard } from './cards/DemGtnSummaryGridCard';
import { DemGtnSummaryStats } from './cards/DemGtnSummaryStats';
import { DemGtnSummaryWaterfallCard } from './cards/DemGtnSummaryWaterfallCard';

/**
 * Gross-to-Net summary by product or by period, with its key figures and a waterfall of the Total column.
 *
 * dataPlugin returns the summary's column fieldDefs and rows, and filtersPlugin the Product and Period filter trees with
 * any default selections. reqParam is sent to both, with the summary's mode and filters added for dataPlugin. title and
 * titleIcon are for when the summary is a page of its own.
 */
export const DemGtnSummary = ({ dataPlugin, filtersPlugin, reqParam, title, titleIcon }) => {
  const rsrc = useRsrc();
  const module = useModuleFromRoute();
  const [filterDims, setFilterDims] = useState();

  useEffect(() => {
    rsrc.data(module, filtersPlugin, reqParam).then((res) => {
      setFilterDims(res);
    });
  }, [filtersPlugin, reqParam]);

  if (!filterDims) return null;

  // Null filters leave the summary unfiltered
  const defaultFormValues = {
    mode: 'PROD',
    prodIds: filterDims.prodDim.defaultIds ?? null,
    periodIds: filterDims.periodDim.defaultIds ?? null,
  };

  const augButtons = (buttons) => {
    // Export only captures charts so far, so it would leave out the summary
    const exportButtonIdx = buttons.findIndex((button) => button.key === 'exportButton');
    buttons.splice(exportButtonIdx, 1);

    buttons.unshift(
      <DemGtnSummaryFilters key="filters" prodDim={filterDims.prodDim} periodDim={filterDims.periodDim} />,
    );
  };

  return (
    <Dashboard title={title} titleIcon={titleIcon} defaultFormValues={defaultFormValues} augButtons={augButtons}>
      <DemGtnSummaryCards dataPlugin={dataPlugin} reqParam={reqParam} />
    </Dashboard>
  );
};

// Separate from DemGtnSummary because the form only exists inside the Dashboard
const DemGtnSummaryCards = ({ dataPlugin, reqParam }) => {
  const module = useModuleFromRoute();
  const { formValues } = useForm();
  const { mode, prodIds, periodIds } = formValues;

  const dataReqParam = useMemo(() => ({ ...reqParam, mode, prodIds, periodIds }), [reqParam, mode, prodIds, periodIds]);

  return (
    <DashboardDataProvider module={module} plugin={dataPlugin} reqParam={dataReqParam}>
      {(data) => {
        if (!data) return null;

        // Without a Total column (e.g. a long range forecast by period) there is nothing for the tiles and waterfall
        const hasTotal = data.fieldDefs.some((fieldDef) => fieldDef.field === 'total');

        return (
          <>
            {hasTotal && <DemGtnSummaryStats />}
            <SpacerLayout>
              <Spacer weights={[3, 2]}>
                <DemGtnSummaryGridCard />
                {hasTotal && <DemGtnSummaryWaterfallCard />}
              </Spacer>
            </SpacerLayout>
          </>
        );
      }}
    </DashboardDataProvider>
  );
};
